/**
 * AmbassadorApplyModal — the brief "apply to be a Brand Ambassador" form shown
 * to logged-in shoppers from the shiny apply tile (home page + dashboard).
 *
 * Collects TikTok / Instagram handles (for admin vetting) and two required
 * agreements — the Ambassador Terms (incl. a binding arbitration clause) and a
 * marketing-rights grant. POSTs to /api/ambassadors/apply; an admin reviews the
 * application before the user is promoted, so on success we tell them to watch
 * their email.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'react-toastify';

export default function AmbassadorApplyModal({ onClose, onApplied }) {
  const [form, setForm] = useState({ tiktokHandle: '', instagramHandle: '', agreedTerms: false, agreedMarketing: false });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));
  const toggle = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.checked }));

  const validate = () => {
    const e = {};
    if (!form.tiktokHandle.trim() && !form.instagramHandle.trim()) {
      e.handles = 'Add at least one — TikTok or Instagram.';
    }
    if (!form.agreedTerms) e.agreedTerms = 'Required to apply.';
    if (!form.agreedMarketing) e.agreedMarketing = 'Required to apply.';
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    try {
      await api.post('/ambassadors/apply', {
        tiktokHandle: form.tiktokHandle || undefined,
        instagramHandle: form.instagramHandle || undefined,
        agreedTerms: true,
        agreedMarketing: true,
      });
      toast.success('Application submitted! Check your email — an admin will review it.');
      onApplied?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not submit your application');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto"
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden my-auto"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="relative px-6 py-5 text-white overflow-hidden"
          style={{ background: 'linear-gradient(135deg,#ec4899 0%,#be185d 55%,#7a1746 100%)' }}>
          <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-white/15 blur-2xl" />
          <button onClick={onClose} className="absolute top-3 right-4 text-white/80 hover:text-white text-2xl leading-none">×</button>
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-white/80 relative">Brand Ambassador</div>
          <h2 className="font-display text-3xl font-semibold mt-1 relative">Apply in 60 seconds</h2>
          <p className="text-white/85 text-sm mt-1 relative">
            Tell us where you post. An admin reviews every application personally.
          </p>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          {/* Social handles */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">TikTok username</label>
            <div className="flex items-center input-field gap-1 px-3">
              <span className="text-gray-400">@</span>
              <input value={form.tiktokHandle} onChange={set('tiktokHandle')} placeholder="yourhandle"
                className="flex-1 bg-transparent border-0 focus:ring-0 outline-none p-0 py-2" maxLength={120} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Instagram username</label>
            <div className="flex items-center input-field gap-1 px-3">
              <span className="text-gray-400">@</span>
              <input value={form.instagramHandle} onChange={set('instagramHandle')} placeholder="yourhandle"
                className="flex-1 bg-transparent border-0 focus:ring-0 outline-none p-0 py-2" maxLength={120} />
            </div>
          </div>
          {errors.handles && <p className="text-red-500 text-xs -mt-2">{errors.handles}</p>}

          {/* Compact legal / agreements */}
          <div className="rounded-xl bg-gray-50 border border-gray-200 p-3 space-y-2.5">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" checked={form.agreedTerms} onChange={toggle('agreedTerms')}
                className="mt-0.5 accent-tea-600 h-4 w-4 shrink-0" />
              <span className="text-[11px] leading-snug text-gray-600">
                I agree to the{' '}
                <Link to="/terms" target="_blank" className="text-tea-600 underline">Ambassador Terms</Link>, including the
                <strong className="text-gray-700"> binding arbitration clause &amp; class-action waiver</strong>.
              </span>
            </label>
            {errors.agreedTerms && <p className="text-red-500 text-[11px] ml-6">{errors.agreedTerms}</p>}
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" checked={form.agreedMarketing} onChange={toggle('agreedMarketing')}
                className="mt-0.5 accent-tea-600 h-4 w-4 shrink-0" />
              <span className="text-[11px] leading-snug text-gray-600">
                I grant AB8 Beauty <strong className="text-gray-700">marketing rights</strong> to use my name, handles,
                and content I create to promote the brand.
              </span>
            </label>
            {errors.agreedMarketing && <p className="text-red-500 text-[11px] ml-6">{errors.agreedMarketing}</p>}
          </div>

          <button type="submit" disabled={submitting} className="btn-primary w-full py-3">
            {submitting ? 'Submitting…' : 'Submit application'}
          </button>
          <p className="text-center text-[11px] text-gray-400">
            We&rsquo;ll email you when an admin has reviewed your application.
          </p>
        </form>
      </div>
    </div>
  );
}
